import ExpenseCorePanel from './ExpenseCorePanel';
import { useCallback, useEffect, useId, useRef, useState } from 'react';
import { Link, useParams, useSearchParams } from 'react-router-dom';
import { mapApiErrorToUiModel, resolveUiErrorCopy } from '@formosa/shared/i18n/mapApiErrorToUiModel';
import { resolveSafeUserVisibleError } from '@formosa/shared/i18n/safeUserVisibleError';
import { journeyApi } from '../../api/journeyApi';
import { journeyBudgetApi } from '../../api/journeyBudgetApi';
import { journeyExpenseApi } from '../../api/journeyExpenseApi';
import { useTravelTranslation } from '../../app/TravelLocaleContext';
import { useAuth } from '../../auth/AuthContext';
import useMemberDisplayNames from '../member-display/useMemberDisplayNames';
import { collectMemberIds, memberFallbackLabel } from '../member-display/memberDisplayModel';
import { BUDGET_CATEGORY_CODES, budgetCategoryLabel } from '../finance/budgetCategory';
import { formatMoneyDisplay } from '../finance/financeMoney';
import { normalizeJourneyBudget } from '../journey-budget/journeyBudgetModel';
import { WORKSPACE_PRODUCT_CHROME } from '../journey-workspace/journeyWorkspacePresentation';
import {
  classifyJourneyAccessError,
  normalizeJourneyWorkspace,
} from '../journey-workspace/journeyWorkspaceModel';
import JourneyWorkspaceNav from '../journey-workspace/JourneyWorkspaceNav';
import { ConfirmDialog, ErrorState, LoadingState } from '../../components/ui';
import { CostDisclaimer } from '../journey-cost';
import {
  activeJourneyMembers,
  buildExpenseCreatePayload,
  buildExpenseUpdatePayload,
  buildExpenseVoidPayload,
  canMutateExpense,
  canRecordExpense,
  canShareExpenseToChat,
  classifyExpenseError,
  createClientRequestId,
  draftFromExpense,
  emptyExpenseDraft,
  equalSplitPreview,
  EXPENSE_CHAT_SHARE_CONFIRM,
  EXPENSE_SPLIT_METHODS,
  EXPENSE_STATUS_FILTERS,
  expenseSplitMethodLabel,
  expenseStatusLabel,
  memberDisplayLabel,
  normalizeExpenseDetail,
  normalizeExpenseList,
  normalizeExpenseSummary,
  validateExpenseDraft,
} from './journeyExpenseModel';
import '../journey-workspace/journey-workspace.css';
import '../journey-workspace/journey-logistics.css';
import '../journey-cost/journey-cost.css';
import './journey-expense.css';

const WRITE_ROLES = new Set(['OWNER', 'COORDINATOR', 'MEMBER']);
const WRITABLE_LIFECYCLES = new Set(['DRAFT', 'READY', 'TRAVELING', 'COMPLETED']);

export default function JourneyExpensesPage(){const [params]=useSearchParams();const group=params.get('groupTripId');return group?<ExpenseCorePanel groupTripId={group}/>:<LegacyJourneyExpensesPage/>;}
function LegacyJourneyExpensesPage() {
  const { journeyId } = useParams();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { t } = useTravelTranslation();
  // 語系切換只重新渲染，不得觸發重新載入或清掉編輯中的草稿。
  const tRef = useRef(t);
  tRef.current = t;
  const viewerMemberId = user?.memberId ?? user?.id;
  const generation = useRef(0);
  const mutating = useRef(false);
  const chatShareLock = useRef(false);
  const createRequestIdRef = useRef(null);

  const [coreGroups,setCoreGroups]=useState(null);
  const [coreGroupError,setCoreGroupError]=useState('');
  const [phase, setPhase] = useState('loading');
  const [errorModel, setErrorModel] = useState(null);
  const [journey, setJourney] = useState(null);
  const [budget, setBudget] = useState(null);
  const [expenses, setExpenses] = useState([]);
  const [summary, setSummary] = useState(null);
  const [mode, setMode] = useState('view'); // view | create | edit
  const [draft, setDraft] = useState(null);
  const [editingExpense, setEditingExpense] = useState(null);
  const [busy, setBusy] = useState(false);
  const [formError, setFormError] = useState('');
  const [confirmSave, setConfirmSave] = useState(false);
  const [voidTarget, setVoidTarget] = useState(null);
  const [voidReason, setVoidReason] = useState('作廢支出');
  const [chatShareNotice, setChatShareNotice] = useState('');
  const [sharingExpenseId, setSharingExpenseId] = useState('');
  const [chatSharePendingExpense, setChatSharePendingExpense] = useState(null);
  const [statusFilter, setStatusFilter] = useState('ALL');
  const titleId = useId();
  const amountId = useId();
  const categoryId = useId();
  const payerId = useId();
  const occurredId = useId();
  const noteId = useId();
  const voidReasonId = useId();
  const memberSummaries = useMemberDisplayNames(collectMemberIds([
    ...(journey?.members || []).map((member) => member.memberId),
    ...expenses.map((expense) => expense.payerMemberId),
    ...expenses.map((expense) => expense.recordedByMemberId),
    ...expenses.flatMap((expense) => (expense.participants || []).map((row) => row.memberId)),
  ]));

  const load = useCallback(async ({ soft = false } = {}) => {
    const requestId = ++generation.current;
    if (!soft) {
      setPhase('loading');
      setErrorModel(null);
      setMode('view');
      setDraft(null);
      setEditingExpense(null);
      setConfirmSave(false);
      setVoidTarget(null);
    }
    setFormError('');
    try {
      const detailPayload = await journeyApi.getDetail(journeyId);
      if (requestId !== generation.current) return;
      const nextJourney = normalizeJourneyWorkspace(detailPayload, viewerMemberId);
      setJourney(nextJourney);

      let budgetPayload = null;
      try {
        budgetPayload = await journeyBudgetApi.get(journeyId);
      } catch (cause) {
        if (requestId !== generation.current) return;
        const kind = classifyExpenseError(cause);
        if (kind === 'budget-not-found') {
          setBudget(null);
          setExpenses([]);
          setSummary(null);
          setPhase('needs-budget');
          return;
        }
        throw cause;
      }
      if (requestId !== generation.current) return;
      const nextBudget = normalizeJourneyBudget(budgetPayload);
      setBudget(nextBudget);

      const [listPayload, summaryPayload] = await Promise.all([
        journeyExpenseApi.list(journeyId),
        journeyExpenseApi.getSummary(journeyId),
      ]);
      if (requestId !== generation.current) return;
      setExpenses(normalizeExpenseList(listPayload));
      setSummary(normalizeExpenseSummary(summaryPayload));
      setPhase('ready');
    } catch (cause) {
      if (requestId !== generation.current) return;
      const kind = classifyExpenseError(cause);
      const access = classifyJourneyAccessError(cause);
      if (kind === 'journey-not-found' || access === 'not-found') setPhase('not-found');
      else if (kind === 'forbidden' || access === 'forbidden') setPhase('forbidden');
      else {
        setErrorModel(mapApiErrorToUiModel(cause, { t: tRef.current }));
        setPhase('error');
      }
    }
  }, [journeyId, viewerMemberId]);

  useEffect(() => {
    if (!/^[1-9]\d*$/.test(journeyId || '')) {
      setPhase('not-found');
      return undefined;
    }
    load();
    return () => { generation.current += 1; };
  }, [journeyId, load]);

  const focusExpenseId = searchParams.get('expense') || '';
  useEffect(() => {
    if (phase !== 'ready' || !focusExpenseId) return undefined;
    const node = document.getElementById(`expense-${focusExpenseId}`);
    if (node?.scrollIntoView) node.scrollIntoView({ block: 'nearest' });
    return undefined;
  }, [phase, focusExpenseId, expenses]);

  const activeMembers = activeJourneyMembers(journey?.members);
  const lifecycleWritable = WRITABLE_LIFECYCLES.has(journey?.lifecycleStatus);
  const roleWritable = WRITE_ROLES.has(journey?.requesterRole);
  const canOpenCreate = lifecycleWritable && roleWritable && Boolean(budget);
  const canEditCurrent = editingExpense
    ? canMutateExpense({
      role: journey?.requesterRole,
      lifecycleStatus: journey?.lifecycleStatus,
      recordedByMemberId: editingExpense.recordedByMemberId,
      viewerMemberId,
    })
    : false;

  function memberLabel(memberId) {
    const member = activeMembers.find((row) => String(row.memberId) === String(memberId))
      || (journey?.members || []).find((row) => String(row.memberId) === String(memberId));
    return member
      ? memberDisplayLabel(member, memberSummaries)
      : memberFallbackLabel(memberId);
  }

  function beginCreate() {
    if (!budget || !canOpenCreate) return;
    setFormError('');
    setConfirmSave(false);
    setVoidTarget(null);
    setEditingExpense(null);
    createRequestIdRef.current = createClientRequestId();
    const defaultPayer = journey?.requesterRole === 'MEMBER'
      ? String(viewerMemberId)
      : String(viewerMemberId || activeMembers[0]?.memberId || '');
    setDraft(emptyExpenseDraft({
      currencyCode: budget.baseCurrency,
      payerMemberId: defaultPayer,
      participantMemberIds: activeMembers.map((row) => String(row.memberId)),
    }));
    setMode('create');
  }

  async function beginEdit(expense) {
    if (!budget || expense?.status === 'VOIDED') return;
    setFormError('');
    setConfirmSave(false);
    setVoidTarget(null);
    const requestId = generation.current;
    setBusy(true);
    try {
      const detailPayload = await journeyExpenseApi.get(journey.journeyId, expense.expenseId);
      if (requestId !== generation.current) return;
      const detail = normalizeExpenseDetail(detailPayload);
      setEditingExpense(detail);
      setDraft(draftFromExpense({
        ...detail,
        currencyCode: budget.baseCurrency,
      }));
      setMode('edit');
    } catch (cause) {
      if (requestId !== generation.current) return;
      setFormError(
        resolveSafeUserVisibleError(cause, t, 'travel.common.loadFailed')
          || 'Expense detail could not be loaded.',
      );
    } finally {
      setBusy(false);
    }
  }

  function requestShareExpenseToChat(expense) {
    if (chatShareLock.current || !canShareExpenseToChat({
      role: journey?.requesterRole,
      lifecycleStatus: journey?.lifecycleStatus,
    })) return;
    setChatSharePendingExpense(expense);
  }

  async function confirmShareExpenseToChat() {
    const expense = chatSharePendingExpense;
    if (!expense || chatShareLock.current || !canShareExpenseToChat({
      role: journey?.requesterRole,
      lifecycleStatus: journey?.lifecycleStatus,
    })) return;
    chatShareLock.current = true;
    setSharingExpenseId(expense.expenseId);
    setChatShareNotice('');
    setFormError('');
    try {
      await journeyExpenseApi.shareToChat(journey.journeyId, expense.expenseId, { confirm: true });
      setChatShareNotice(`「${expense.title}」已分享到旅程聊天室`);
      setChatSharePendingExpense(null);
    } catch (cause) {
      const kind = classifyExpenseError(cause);
      if (kind === 'forbidden') setFormError('你沒有權限分享這筆支出。');
      else if (kind === 'unavailable') setFormError('聊天室暫時無法使用。');
      else {
        setFormError(
          resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed')
            || '分享到旅程聊天室失敗。',
        );
      }
    } finally {
      chatShareLock.current = false;
      setSharingExpenseId('');
    }
  }

  function cancelForm() {
    setFormError('');
    setConfirmSave(false);
    setDraft(null);
    setEditingExpense(null);
    setMode('view');
    createRequestIdRef.current = null;
  }

  function toggleParticipant(memberId) {
    const id = String(memberId);
    setDraft((current) => {
      if (current.splitMethod === 'PAYER_ONLY') return current;
      const selected = new Set(current.participantMemberIds || []);
      const nextValues = { ...(current.participantValues || {}) };
      if (selected.has(id)) {
        selected.delete(id);
        delete nextValues[id];
      } else {
        selected.add(id);
      }
      return {
        ...current,
        participantMemberIds: [...selected],
        participantValues: nextValues,
      };
    });
  }

  function selectAllParticipants() {
    setDraft((current) => {
      if (current.splitMethod === 'PAYER_ONLY') return current;
      return {
        ...current,
        participantMemberIds: activeMembers.map((row) => String(row.memberId)),
      };
    });
  }

  function clearParticipants() {
    setDraft((current) => {
      if (current.splitMethod === 'PAYER_ONLY') return current;
      return { ...current, participantMemberIds: [], participantValues: {} };
    });
  }

  function setSplitMethod(nextMethod) {
    setDraft((current) => {
      if (nextMethod === 'PAYER_ONLY') {
        const payer = current.payerMemberId == null ? '' : String(current.payerMemberId);
        return {
          ...current,
          splitMethod: nextMethod,
          participantMemberIds: payer ? [payer] : [],
          participantValues: {},
        };
      }
      return {
        ...current,
        splitMethod: nextMethod,
        participantValues: nextMethod === 'EQUAL' ? {} : (current.participantValues || {}),
      };
    });
  }

  function setParticipantValue(memberId, value) {
    const id = String(memberId);
    setDraft((current) => ({
      ...current,
      participantValues: {
        ...(current.participantValues || {}),
        [id]: value,
      },
    }));
  }

  function mapMutationError(cause) {
    const kind = classifyExpenseError(cause);
    if (kind === 'conflict') {
      return {
        kind,
        message: '旅行支出資料已更新，請重新載入',
      };
    }
    if (kind === 'idempotency-conflict') {
      return { kind, message: 'Idempotency key conflict. Start a new expense.' };
    }
    if (kind === 'voided') {
      return { kind, message: 'This expense is already voided.' };
    }
    if (kind === 'validation') {
      return { kind, message: 'Please check the expense values.' };
    }
    if (kind === 'forbidden') {
      return { kind, message: 'You do not have permission to change this expense.' };
    }
    if (kind === 'lifecycle') {
      return { kind, message: 'Expense cannot be changed in the current journey status.' };
    }
    if (kind === 'budget-not-found') {
      return { kind, message: '請先設定旅行預算' };
    }
    return {
      kind,
      message: resolveSafeUserVisibleError(cause, t, 'travel.common.actionFailed') || 'Expense save failed.',
    };
  }

  async function runSave() {
    if (mutating.current || !draft || !budget) return;
    if (mode === 'edit' && !canEditCurrent) {
      setFormError('You do not have permission to edit this expense.');
      return;
    }
    if (mode === 'create' && !canRecordExpense({
      role: journey?.requesterRole,
      lifecycleStatus: journey?.lifecycleStatus,
      payerMemberId: draft.payerMemberId,
      viewerMemberId,
    })) {
      setFormError('You do not have permission to record this expense.');
      setConfirmSave(false);
      return;
    }
    const validation = validateExpenseDraft(draft);
    if (validation) {
      setFormError(validation);
      setConfirmSave(false);
      return;
    }
    mutating.current = true;
    setBusy(true);
    setFormError('');
    const requestId = generation.current;
    try {
      if (mode === 'create') {
        if (!createRequestIdRef.current) {
          createRequestIdRef.current = createClientRequestId();
        }
        await journeyExpenseApi.create(
          journey.journeyId,
          buildExpenseCreatePayload(draft, {
            expectedBudgetRowVersion: budget.rowVersion,
            clientRequestId: createRequestIdRef.current,
          }),
        );
        createRequestIdRef.current = null;
      } else {
        await journeyExpenseApi.update(
          journey.journeyId,
          editingExpense.expenseId,
          buildExpenseUpdatePayload(draft, {
            expectedBudgetRowVersion: budget.rowVersion,
          }),
        );
      }
      if (requestId !== generation.current) return;
      setConfirmSave(false);
      setDraft(null);
      setEditingExpense(null);
      setMode('view');
      await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const mapped = mapMutationError(cause);
      setFormError(mapped.message);
      setConfirmSave(false);
      if (mapped.kind === 'conflict') {
        setMode('view');
        setDraft(null);
        setEditingExpense(null);
      }
      // Keep createRequestIdRef on create failure so retry reuses the same idempotency key.
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  async function runVoid() {
    if (mutating.current || !voidTarget || !budget) return;
    const reason = String(voidReason || '').trim();
    if (!reason) {
      setFormError('Void reason is required.');
      return;
    }
    const allowed = canMutateExpense({
      role: journey?.requesterRole,
      lifecycleStatus: journey?.lifecycleStatus,
      recordedByMemberId: voidTarget.recordedByMemberId,
      viewerMemberId,
    });
    if (!allowed) {
      setFormError('You do not have permission to void this expense.');
      return;
    }
    mutating.current = true;
    setBusy(true);
    setFormError('');
    const requestId = generation.current;
    try {
      await journeyExpenseApi.void(
        journey.journeyId,
        voidTarget.expenseId,
        buildExpenseVoidPayload({
          expectedBudgetRowVersion: budget.rowVersion,
          reason,
        }),
      );
      if (requestId !== generation.current) return;
      setVoidTarget(null);
      setVoidReason('作廢支出');
      await load({ soft: true });
    } catch (cause) {
      if (requestId !== generation.current) return;
      const mapped = mapMutationError(cause);
      setFormError(mapped.message);
      if (mapped.kind === 'conflict') {
        setVoidTarget(null);
      }
    } finally {
      mutating.current = false;
      setBusy(false);
    }
  }

  if (phase === 'loading') {
    return (
      <main className="journey-workspace-state">
        <LoadingState label={t('travel.common.loading')} data-testid="expenses-loading" />
      </main>
    );
  }
  if (phase === 'not-found') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.notFoundTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.notFoundBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'forbidden') {
    return (
      <main className="journey-workspace-state">
        <h1>{WORKSPACE_PRODUCT_CHROME.forbiddenTitle}</h1>
        <p>{WORKSPACE_PRODUCT_CHROME.forbiddenBody}</p>
        <Link to="/journeys">{WORKSPACE_PRODUCT_CHROME.backToMyJourneys}</Link>
      </main>
    );
  }
  if (phase === 'error') {
    const errorCopy = resolveUiErrorCopy(errorModel, t);
    return (
      <main className="journey-workspace-state" data-testid="expenses-error">
        <ErrorState
          title="We couldn’t open expenses"
          description={errorCopy.description}
          retryable
          retryLabel="Try again"
          onRetry={load}
        />
      </main>
    );
  }

  const editing = mode === 'create' || mode === 'edit';
  const payerLocked = journey?.requesterRole === 'MEMBER' && mode === 'create';
  const summaryCurrency = summary?.currency || budget?.baseCurrency || '';
  const filteredExpenses = statusFilter === 'ALL'
    ? expenses
    : expenses.filter((row) => row.status === statusFilter);
  const splitMethod = draft?.splitMethod || 'EQUAL';
  const needsValueInput = splitMethod === 'EXACT_AMOUNT'
    || splitMethod === 'PERCENTAGE'
    || splitMethod === 'SHARES';
  const equalPreview = draft && splitMethod === 'EQUAL'
    ? equalSplitPreview(draft.amount, (draft.participantMemberIds || []).length)
    : null;

  return (
    <main className="journey-workspace">
      <header className="journey-workspace__header">
        <div>
          <p className="journey-workspace__eyebrow">{WORKSPACE_PRODUCT_CHROME.eyebrow}</p>
          <h1>{journey?.title || 'Journey'}</h1>
          <p className="journey-workspace__meta">
            {journey?.lifecycleStatusLabel}
            {journey?.requesterRoleLabel ? ` · ${journey.requesterRoleLabel}` : ''}
          </p>
        </div>
        <Link className="journey-workspace__back" to="/journeys">{WORKSPACE_PRODUCT_CHROME.myJourneys}</Link>
      </header>
      <JourneyWorkspaceNav journeyId={journey.journeyId} />
      <section className="expense-core expense-core__entry" aria-label={t('travel.expense.s06.quickEntryEntrance')}><h2>{t('travel.expense.s06.travelExpenses')}</h2><p>{t('travel.expense.s06.entryExplanation')}</p>
        <button type="button" onClick={async()=>{try{const raw=await journeyExpenseApi.coreGroups(journeyId);setCoreGroups(raw?.data?.data??raw?.data??raw);setCoreGroupError('');}catch(e){setCoreGroupError('travel.expense.s06.groupsFailed');}}}>{t('travel.expense.s06.openLedger')}</button>
        {coreGroupError&&<p role="alert">{t(coreGroupError)}</p>}{coreGroups&&<ul>{coreGroups.length?coreGroups.map(g=><li key={g.groupTripId}><Link to={`?groupTripId=${encodeURIComponent(g.groupTripId)}`}>{g.label||t('travel.expense.s06.groupLedger')}</Link></li>):<li>{t('travel.expense.s06.noGroups')}</li>}</ul>}
      </section>

      <section className="journey-workspace__panel journey-expense" aria-labelledby="expense-heading">
        <div className="journey-workspace__panel-head">
          <h2 id="expense-heading">支出</h2>
          {phase === 'ready' && (
            <div className="journey-expense__toolbar">
              <Link to={`/journeys/${journey.journeyId}/split`}>查看分帳</Link>
              {canOpenCreate && !editing && (
                <button type="button" onClick={beginCreate} disabled={busy}>新增支出</button>
              )}
            </div>
          )}
        </div>

        {phase === 'needs-budget' && (
          <div className="journey-expense__needs-budget" role="status">
            <p>請先設定旅行預算</p>
            <Link to={`/journeys/${journey.journeyId}/budget`}>前往預算</Link>
          </div>
        )}

        {phase === 'ready' && (
          <>
            {!editing ? <CostDisclaimer /> : null}
            {chatShareNotice ? (
              <p className="journey-expense__notice" role="status">
                {chatShareNotice}
                {' '}
                <Link to={`/journeys/${journey.journeyId}/chat`}>查看聊天室</Link>
              </p>
            ) : null}
            <dl className="journey-expense__summary">
              <div>
                <dt>進行中合計</dt>
                <dd>
                  {formatMoneyDisplay(summary?.totalAmount || '0')}
                  {summaryCurrency ? ` ${summaryCurrency}` : ''}
                </dd>
              </div>
              <div>
                <dt>進行中</dt>
                <dd>{summary?.activeExpenseCount ?? 0}</dd>
              </div>
              <div>
                <dt>已作廢</dt>
                <dd>{summary?.voidedExpenseCount ?? 0}</dd>
              </div>
              <div>
                <dt>預算幣別</dt>
                <dd>{budget?.baseCurrency || '—'}</dd>
              </div>
            </dl>

            <div
              className="journey-expense__filters"
              role="group"
              aria-label="支出狀態篩選"
              data-testid="expense-status-filters"
            >
              {EXPENSE_STATUS_FILTERS.map((filter) => {
                const label = filter === 'ALL'
                  ? '全部'
                  : expenseStatusLabel(filter);
                return (
                  <button
                    key={filter}
                    type="button"
                    className={statusFilter === filter ? 'is-active' : undefined}
                    aria-pressed={statusFilter === filter}
                    onClick={() => setStatusFilter(filter)}
                  >
                    {label}
                  </button>
                );
              })}
            </div>

            {!lifecycleWritable && (
              <p className="journey-expense__readonly">
                目前行程狀態為唯讀，無法變更支出。
              </p>
            )}
            {lifecycleWritable && !roleWritable && (
              <p className="journey-expense__readonly">
                你的角色無法記錄或變更支出。
              </p>
            )}

            {formError && !editing && !voidTarget && (
              <p className="journey-workspace__error" role="alert">
                {formError}
                {formError.includes('重新載入') && (
                  <>
                    {' '}
                    <button type="button" onClick={() => load()}>Reload</button>
                  </>
                )}
              </p>
            )}

            {voidTarget && (
              <div className="journey-expense__confirm" role="dialog" aria-labelledby="void-expense-title">
                <h3 id="void-expense-title">作廢支出</h3>
                <p>
                  確認作廢
                  {' '}
                  <strong>{voidTarget.title}</strong>
                  ？作廢後仍會保留紀錄，不會永久刪除。
                </p>
                <label htmlFor={voidReasonId}>
                  原因
                  <input
                    id={voidReasonId}
                    value={voidReason}
                    onChange={(event) => setVoidReason(event.target.value)}
                    maxLength={500}
                    disabled={busy}
                  />
                </label>
                {formError && <p className="journey-workspace__error" role="alert">{formError}</p>}
                <div className="journey-expense__actions">
                  <button type="button" className="journey-expense__confirm-primary" onClick={runVoid} disabled={busy}>
                    確認作廢
                  </button>
                  <button
                    type="button"
                    onClick={() => { setVoidTarget(null); setFormError(''); }}
                    disabled={busy}
                  >
                    取消
                  </button>
                </div>
              </div>
            )}

            {editing && draft && (
              <form
                className="journey-expense__form"
                onSubmit={(event) => {
                  event.preventDefault();
                  if (!confirmSave) setConfirmSave(true);
                  else runSave();
                }}
              >
                <h3>{mode === 'create' ? '新增支出' : '編輯支出'}</h3>
                {mode === 'edit' && !canEditCurrent && (
                  <p className="journey-expense__readonly">你可以查看此支出，但無法編輯。</p>
                )}

                <label htmlFor={titleId}>
                  標題
                  <input
                    id={titleId}
                    value={draft.title}
                    onChange={(event) => setDraft({ ...draft, title: event.target.value })}
                    maxLength={100}
                    required
                    disabled={busy || (mode === 'edit' && !canEditCurrent)}
                  />
                </label>

                <label htmlFor={amountId}>
                  金額（
                  {draft.currencyCode}
                  ）
                  <input
                    id={amountId}
                    inputMode="decimal"
                    value={draft.amount}
                    onChange={(event) => setDraft({ ...draft, amount: event.target.value })}
                    required
                    disabled={busy || (mode === 'edit' && !canEditCurrent)}
                  />
                </label>

                <p className="journey-expense__currency-fixed">
                  幣別固定為預算幣別
                  {' '}
                  <strong>{budget.baseCurrency}</strong>
                  。
                </p>

                <label htmlFor={categoryId}>
                  類別
                  <select
                    id={categoryId}
                    value={draft.categoryCode}
                    onChange={(event) => setDraft({ ...draft, categoryCode: event.target.value })}
                    disabled={busy || (mode === 'edit' && !canEditCurrent)}
                  >
                    {BUDGET_CATEGORY_CODES.map((code) => (
                      <option key={code} value={code}>{budgetCategoryLabel(code, t)}</option>
                    ))}
                  </select>
                </label>

                <label htmlFor={payerId}>
                  付款人
                  <select
                    id={payerId}
                    value={draft.payerMemberId}
                    onChange={(event) => {
                      const nextPayer = event.target.value;
                      setDraft((current) => {
                        if (current.splitMethod === 'PAYER_ONLY') {
                          return {
                            ...current,
                            payerMemberId: nextPayer,
                            participantMemberIds: nextPayer ? [String(nextPayer)] : [],
                            participantValues: {},
                          };
                        }
                        return { ...current, payerMemberId: nextPayer };
                      });
                    }}
                    disabled={busy || payerLocked || (mode === 'edit' && !canEditCurrent)}
                  >
                    {activeMembers.map((member) => (
                      <option key={String(member.memberId)} value={String(member.memberId)}>
                        {memberDisplayLabel(member, memberSummaries)}
                      </option>
                    ))}
                  </select>
                </label>

                <label htmlFor={occurredId}>
                  發生時間（UTC）
                  <input
                    id={occurredId}
                    type="datetime-local"
                    value={draft.occurredAt}
                    onChange={(event) => setDraft({ ...draft, occurredAt: event.target.value })}
                    required
                    disabled={busy || (mode === 'edit' && !canEditCurrent)}
                  />
                </label>

                <label htmlFor={noteId}>
                  備註
                  <textarea
                    id={noteId}
                    value={draft.note}
                    onChange={(event) => setDraft({ ...draft, note: event.target.value })}
                    maxLength={1000}
                    rows={3}
                    disabled={busy || (mode === 'edit' && !canEditCurrent)}
                  />
                </label>

                <fieldset className="journey-expense__split-method" disabled={busy || (mode === 'edit' && !canEditCurrent)}>
                  <legend>分攤方式</legend>
                  <div
                    className="journey-expense__method-chips"
                    role="group"
                    aria-label="分攤方式"
                    data-testid="expense-split-method"
                  >
                    {EXPENSE_SPLIT_METHODS.map((method) => (
                      <button
                        key={method}
                        type="button"
                        className={splitMethod === method ? 'is-active' : undefined}
                        aria-pressed={splitMethod === method}
                        onClick={() => setSplitMethod(method)}
                      >
                        {expenseSplitMethodLabel(method)}
                      </button>
                    ))}
                  </div>
                </fieldset>

                <fieldset className="journey-expense__participants" disabled={busy || (mode === 'edit' && !canEditCurrent)}>
                  <legend>分攤對象</legend>
                  <p className="journey-expense__hint">
                    {splitMethod === 'PAYER_ONLY'
                      ? '僅付款人承擔此筆支出。'
                      : '勾選分攤對象；付款人不一定要是分攤對象。最終分攤金額以伺服器計算為準。'}
                  </p>
                  {splitMethod !== 'PAYER_ONLY' ? (
                    <div className="journey-expense__participant-actions">
                      <button type="button" onClick={selectAllParticipants}>全選</button>
                      <button type="button" onClick={clearParticipants}>清除</button>
                    </div>
                  ) : null}
                  <ul className="journey-expense__participant-list">
                    {activeMembers.map((member) => {
                      const id = String(member.memberId);
                      const checked = (draft.participantMemberIds || []).includes(id);
                      const valueId = `${amountId}-split-${id}`;
                      if (splitMethod === 'PAYER_ONLY') {
                        const isPayer = id === String(draft.payerMemberId);
                        if (!isPayer) return null;
                        return (
                          <li key={id}>
                            <span>{memberDisplayLabel(member, memberSummaries)}</span>
                            <span className="journey-expense__hint">僅付款人</span>
                          </li>
                        );
                      }
                      return (
                        <li key={id}>
                          <label>
                            <input
                              type="checkbox"
                              checked={checked}
                              onChange={() => toggleParticipant(id)}
                            />
                            {memberDisplayLabel(member, memberSummaries)}
                          </label>
                          {needsValueInput && checked ? (
                            <label htmlFor={valueId} className="journey-expense__value-input">
                              {splitMethod === 'EXACT_AMOUNT' && '金額'}
                              {splitMethod === 'PERCENTAGE' && '百分比'}
                              {splitMethod === 'SHARES' && '份數'}
                              <input
                                id={valueId}
                                inputMode="decimal"
                                value={draft.participantValues?.[id] || ''}
                                onChange={(event) => setParticipantValue(id, event.target.value)}
                                placeholder={splitMethod === 'PERCENTAGE' ? '0–100' : '0.00'}
                              />
                            </label>
                          ) : null}
                        </li>
                      );
                    })}
                  </ul>
                  <p className="journey-expense__hint" data-testid="expense-split-preview">
                    分攤預覽（最終以伺服器結果為準）
                    {equalPreview
                      ? ` · 均分約 ${equalPreview.shareDisplay} ${draft.currencyCode}／人（${equalPreview.participantCount} 人）`
                      : null}
                  </p>
                </fieldset>

                {formError && <p className="journey-workspace__error" role="alert">{formError}</p>}

                {confirmSave ? (
                  <div className="journey-expense__confirm" role="status">
                    <p>
                      {mode === 'create'
                        ? '確認記錄這筆支出？'
                        : '確認更新這筆支出？'}
                    </p>
                    <div className="journey-expense__actions">
                      <button
                        type="submit"
                        className="journey-expense__confirm-primary"
                        disabled={busy || (mode === 'edit' && !canEditCurrent)}
                      >
                        確認
                      </button>
                      <button type="button" onClick={() => setConfirmSave(false)} disabled={busy}>
                        返回
                      </button>
                      <button type="button" onClick={cancelForm} disabled={busy}>取消</button>
                    </div>
                  </div>
                ) : (
                  <div className="journey-expense__actions">
                    <button
                      type="submit"
                      disabled={busy || (mode === 'edit' && !canEditCurrent)}
                    >
                      {mode === 'create' ? '儲存支出' : '儲存變更'}
                    </button>
                    <button type="button" onClick={cancelForm} disabled={busy}>取消</button>
                  </div>
                )}
              </form>
            )}

            {!editing && filteredExpenses.length === 0 && (
              <div className="journey-expense__empty">
                <p>{expenses.length === 0 ? '尚未記錄支出' : '沒有符合篩選的支出'}</p>
                {canOpenCreate && expenses.length === 0 && (
                  <button type="button" onClick={beginCreate} disabled={busy}>
                    新增第一筆支出
                  </button>
                )}
              </div>
            )}

            {!editing && filteredExpenses.length > 0 && (
              <ul className="journey-expense__list">
                {filteredExpenses.map((expense) => {
                  const voided = expense.status === 'VOIDED';
                  const canMutate = canMutateExpense({
                    role: journey?.requesterRole,
                    lifecycleStatus: journey?.lifecycleStatus,
                    recordedByMemberId: expense.recordedByMemberId,
                    viewerMemberId,
                  });
                  const canChatShare = canShareExpenseToChat({
                    role: journey?.requesterRole,
                    lifecycleStatus: journey?.lifecycleStatus,
                  });
                  const sharing = sharingExpenseId === expense.expenseId;
                  return (
                    <li
                      key={expense.expenseId}
                      id={`expense-${expense.expenseId}`}
                      className={voided ? 'journey-expense__item is-voided' : 'journey-expense__item'}
                    >
                      <div className="journey-expense__item-main">
                        <h3>{expense.title}</h3>
                        <p className="journey-expense__amount">
                          {formatMoneyDisplay(expense.amount)}
                          {' '}
                          {expense.currencyCode}
                        </p>
                        <p>
                          {budgetCategoryLabel(expense.categoryCode, t)}
                          {' · '}
                          {memberLabel(expense.payerMemberId)}
                        </p>
                        <p>
                          {expense.occurredAt || '—'}
                          {' · '}
                          <span
                            className={`journey-expense__status is-${String(expense.status || '').toLowerCase()}`}
                          >
                            {expense.statusLabel || expenseStatusLabel(expense.status)}
                          </span>
                        </p>
                      </div>
                      <div className="journey-expense__item-actions">
                        {!voided && (
                          <button
                            type="button"
                            onClick={() => beginEdit(expense)}
                            disabled={busy}
                          >
                            {canMutate ? '編輯' : '查看'}
                          </button>
                        )}
                        {!voided && canMutate && (
                          <button
                            type="button"
                            onClick={() => {
                              setFormError('');
                              setVoidReason('作廢支出');
                              setVoidTarget(expense);
                            }}
                            disabled={busy}
                          >
                            作廢支出
                          </button>
                        )}
                        {canChatShare ? (
                          <button
                            type="button"
                            onClick={() => requestShareExpenseToChat(expense)}
                            disabled={busy || sharing || Boolean(sharingExpenseId)}
                          >
                            {sharing ? '分享中…' : '分享到旅程聊天室'}
                          </button>
                        ) : null}
                      </div>
                    </li>
                  );
                })}
              </ul>
            )}
          </>
        )}
      </section>

      <ConfirmDialog
        open={Boolean(chatSharePendingExpense)}
        title="分享到旅程聊天室"
        description={EXPENSE_CHAT_SHARE_CONFIRM}
        confirmLabel="分享到聊天室"
        cancelLabel={WORKSPACE_PRODUCT_CHROME.cancel}
        loading={Boolean(sharingExpenseId)}
        onCancel={() => {
          if (!sharingExpenseId) setChatSharePendingExpense(null);
        }}
        onConfirm={confirmShareExpenseToChat}
      />
    </main>
  );
}
